import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarIcon, Download, Mail, Mic, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, errText } from "@/lib/api";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { BriefDraftReview } from "@/components/BriefDraftReview";
import { BriefItemGroup } from "@/components/BriefItemGroup";
import { BriefListing } from "@/components/BriefListing";
import { BriefSendDialog } from "@/components/BriefSendDialog";
import { DayTimeline } from "@/components/DayTimeline";
import { Letterhead } from "@/components/Letterhead";
import { downloadAssociateBriefPdf } from "@/lib/exportSheet";
import { addDays, isoDate, longDate, parseISO } from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";

const today = () => isoDate(new Date());
const tomorrow = () => isoDate(addDays(new Date(), 1));

export default function AssociateBrief() {
  const { canWrite, workspace } = useAuth();
  const [params, setParams] = useSearchParams();
  const [date, setDate] = useState(params.get("date") || tomorrow());
  const [brief, setBrief] = useState(null);
  const [draft, setDraft] = useState(null);
  const [matters, setMatters] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [inbound, setInbound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, notes] = await Promise.all([
        api.get("/brief", { params: { date } }),
        api.get("/voice/notes", { params: { date, status: "review" } }),
      ]);
      setBrief(b.data);
      setDraft(notes.data[0] || null);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [date, workspace?.workspace_id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const wanted = params.get("date");
    if (wanted && wanted !== date) setDate(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    api.get("/matters").then(({ data }) => setMatters(data)).catch(() => setMatters([]));
    api.get("/associates").then(({ data }) => setRecipients(data)).catch(() => setRecipients([]));
    api.get("/inbound/status").then(({ data }) => setInbound(data)).catch(() => setInbound(null));
  }, [workspace?.workspace_id]);

  const upload = async (file) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("target_date", date);
      const { data } = await api.post("/voice/notes", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDraft(data);
      toast.success(`Heard ${data.items?.length || 0} instruction(s) — review and save`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const counts = brief?.counts || { listings: 0, instructions: 0 };

  return (
    <div className="space-y-8" data-testid="associate-brief-page">
      <Letterhead />

      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="label-caps">Associate brief</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-2">{longDate(date)}</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {counts.listings} listing(s) · {counts.instructions} instruction(s)
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            data-testid="brief-pdf-btn"
            onClick={() => downloadAssociateBriefPdf(date).catch((e) => toast.error(errText(e)))}
            className="rounded-full h-12 px-5"
          >
            <Download className="h-5 w-5 mr-2" /> Brief PDF
          </Button>
          {canWrite ? (
            <Button
              data-testid="brief-email-btn"
              onClick={() => setSendOpen(true)}
              className="rounded-full h-12 px-6"
            >
              <Send className="h-5 w-5 mr-2" /> Email associates
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="brief-date-switch">
        {[
          ["Today", today()],
          ["Tomorrow", tomorrow()],
        ].map(([label, value]) => (
          <button
            key={label}
            data-testid={`brief-date-${label.toLowerCase()}`}
            onClick={() => {
              setDate(value);
              setParams({ date: value });
            }}
            className={`px-5 py-3 rounded-sm border text-base font-medium transition-colors duration-200 ${
              date === value
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border hover:bg-accent"
            }`}
          >
            {label}
          </button>
        ))}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              data-testid="brief-date-trigger"
              className="rounded-sm h-12 px-5 text-base font-normal"
            >
              <CalendarIcon className="h-4 w-4 mr-2" /> Pick a date
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0">
            <Calendar
              mode="single"
              selected={parseISO(date)}
              onSelect={(d) => {
                if (!d) return;
                setDate(isoDate(d));
                setParams({ date: isoDate(d) });
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {canWrite ? (
        <section className="surface p-6" data-testid="voice-note-panel">
          <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
            <Mic className="h-5 w-5" /> Dictate the day's instructions
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
            Speak matter by matter in English, Hindi or a mix. Each instruction is matched to the
            listing it belongs to, with the Bare Acts and judgments the associate must carry. Nothing
            is saved until you approve it.
          </p>
          <div className="mt-5">
            <VoiceRecorder onAudio={upload} busy={busy} />
          </div>
          {inbound ? (
            <div
              className="mt-5 border border-border rounded-sm px-4 py-3 flex flex-wrap items-center gap-2"
              data-testid="inbound-status"
            >
              <Mail className="h-4 w-4 shrink-0" />
              {inbound.enabled ? (
                <p className="text-sm">
                  Or email a voice note from <strong>{inbound.send_from}</strong> to{" "}
                  <strong>{inbound.diary_address || inbound.todo_address}</strong> — emailed notes
                  save straight into the brief.
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Voice notes by email are ready to switch on — set it up under Settings → Email in.
                </p>
              )}
            </div>
          ) : null}
        </section>
      ) : null}

      {draft ? (
        <BriefDraftReview
          key={draft.note_id}
          note={draft}
          matters={matters}
          onSaved={() => {
            setDraft(null);
            load();
          }}
          onDiscarded={() => {
            setDraft(null);
            load();
          }}
        />
      ) : null}

      {brief ? (
        <div className="space-y-4" data-testid="brief-body">
          <BriefItemGroup
            testId="brief-general-items"
            title="General instructions — for the whole day"
            hint="Anything that is not tied to one listing, such as reaching court early or collecting files."
            items={brief.general_items}
            date={date}
            isGeneral
            canWrite={canWrite}
            onChange={load}
          />

          <BriefItemGroup
            testId="brief-extra-items"
            title="Unassigned instructions"
            hint="Instructions that belong to no listing on this date."
            items={brief.extra_items}
            date={date}
            isGeneral={false}
            canWrite={canWrite}
            onChange={load}
          />

          {brief.listings.length ? (
            <section className="surface p-6" data-testid="brief-timeline">
              <p className="label-caps">The day at a glance</p>
              <div className="mt-3">
                <DayTimeline events={brief.listings} />
              </div>
            </section>
          ) : null}

          {brief.listings.length ? (
            brief.listings.map((listing) => (
              <BriefListing
                key={listing.event_id}
                listing={listing}
                date={date}
                canWrite={canWrite}
                onChange={load}
              />
            ))
          ) : (
            <p className="surface p-6 text-sm text-muted-foreground" data-testid="brief-empty">
              Nothing is listed on this date, so there is no brief to send yet.
            </p>
          )}
        </div>
      ) : (
        <div className="h-48 surface animate-pulse" data-testid="brief-loading" />
      )}

      <BriefSendDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        date={date}
        recipients={recipients}
        onSent={load}
      />
    </div>
  );
}
